<template>
  <HrPageLayout
    title="Declaration of income, assets and liabilities"
    :subtitle="pageSubtitle"
    icon=""
    :breadcrumb-items="[
      { label: 'Home', route: '/employee-dashboard' },
      { label: 'Employee Self-Service' },
      { label: 'COIA declaration' }
    ]"
  >
    <!-- My submissions -->
    <div v-if="pageMode === 'list'" class="ska-list">
      <div class="ska-hero">
        <p class="ska-lead">
          Access your declarations by year.
          <strong>Draft</strong> and <strong>rejected</strong> submissions can be edited here.
          <strong>Pending</strong> or <strong>approved</strong> declarations are view-only online; contact HR for changes.
        </p>
      </div>
      <div class="ska-list-toolbar">
        <button type="button" class="ska-btn ska-btn--primary" @click="startNewDeclaration">
          <i class="bx bx-plus-circle"></i> New declaration
        </button>
      </div>
      <div v-if="loadingList" class="ska-muted">Loading your records…</div>
      <div v-else class="ska-table-wrap">
        <table class="ska-table" aria-label="My wealth declarations">
          <thead>
            <tr>
              <th>Year</th>
              <th>Status</th>
              <th>Last updated</th>
              <th>Submitted</th>
              <th class="ska-th-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in myRecords" :key="r.id">
              <td class="ska-year">{{ r.declaration_year }}</td>
              <td><span class="ska-pill" :class="statusPillClass(r.status)">{{ formatStatus(r.status) }}</span></td>
              <td>{{ formatDt(r.updated_at) }}</td>
              <td>{{ r.submitted_at ? formatDt(r.submitted_at) : "—" }}</td>
              <td class="ska-actions">
                <button
                  v-if="isEditableStatus(r.status)"
                  type="button"
                  class="ska-btn ska-btn--sm ska-btn--outline"
                  @click="openRecord(r)"
                >
                  Edit
                </button>
                <button
                  v-else
                  type="button"
                  class="ska-btn ska-btn--sm ska-btn--ghost"
                  @click="openRecord(r)"
                >
                  View
                </button>
              </td>
            </tr>
            <tr v-if="!myRecords.length">
              <td colspan="5" class="ska-empty">No declarations saved yet. Click <strong>New declaration</strong> to begin.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form -->
    <div v-else class="ska-form">
      <div class="card mb-3 ska-toolbar-card">
        <div class="card-body row g-3 align-items-end">
          <div class="col-md-4">
            <button type="button" class="ska-btn ska-btn--ghost" @click="backToList">
              <i class="bx bx-arrow-back"></i> My submissions
            </button>
          </div>
          <div class="col-md-4">
            <label class="form-label">Declaration year</label>
            <select v-model.number="declarationYear" class="form-select" @change="loadDeclarationData">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="col-md-4 text-md-end">
            <span v-if="status && status !== 'none'" class="ska-pill ska-pill--inline" :class="statusPillClass(status)">{{ statusLabel }}</span>
          </div>
        </div>
      </div>

      <div v-if="loadingForm" class="text-center py-4 ska-muted">Loading declaration…</div>
      <template v-else>
        <p v-if="notificationMessage" class="notice-status-change">{{ notificationMessage }}</p>

        <div v-if="status === 'Rejected' && rejectionNote" class="alert alert-danger mb-3" role="status">
          <strong>Returned / rejected.</strong>
          <span class="d-block mt-1">{{ rejectionNote }}</span>
        </div>

        <p v-if="canEdit && status === 'Rejected'" class="notice-rejected">
          This declaration was rejected. Update the form, save draft, then submit again.
        </p>
        <p v-else-if="!canEdit" class="notice-readonly">
          This declaration is view-only (submitted or approved). For changes, contact department HR or county compliance.
        </p>

        <div class="coia-section">
          <h3>1. Spouse details</h3>
          <div v-for="(s, i) in form.spouses" :key="'sp-' + i" class="person-row">
            <input v-model="s.surname" type="text" class="form-control" placeholder="Surname" :disabled="!canEdit">
            <input v-model="s.first_name" type="text" class="form-control" placeholder="First name" :disabled="!canEdit">
            <input v-model="s.other_names" type="text" class="form-control" placeholder="Other names" :disabled="!canEdit">
            <button v-if="canEdit && form.spouses.length > 1" type="button" class="btn-mini" @click="form.spouses.splice(i, 1)">Remove</button>
          </div>
          <button v-if="canEdit" type="button" class="btn-link" @click="form.spouses.push({ surname: '', first_name: '', other_names: '' })">+ Add spouse</button>
        </div>

        <div class="coia-section">
          <h3>2. Dependent children (under 18)</h3>
          <div v-for="(d, i) in form.dependents" :key="'dep-' + i" class="person-row">
            <input v-model="d.surname" type="text" class="form-control" placeholder="Surname" :disabled="!canEdit">
            <input v-model="d.first_name" type="text" class="form-control" placeholder="First name" :disabled="!canEdit">
            <input v-model="d.other_names" type="text" class="form-control" placeholder="Other names" :disabled="!canEdit">
            <button v-if="canEdit && form.dependents.length > 1" type="button" class="btn-mini" @click="form.dependents.splice(i, 1)">Remove</button>
          </div>
          <button v-if="canEdit" type="button" class="btn-link" @click="form.dependents.push({ surname: '', first_name: '', other_names: '' })">+ Add dependent</button>
        </div>

        <div v-for="(fs, fi) in form.financial_statements" :key="'fs-' + fi" class="coia-section fin-block">
          <h3>3. Financial statement — {{ fs.subject_label || fs.subject }}</h3>
          <p class="hint">A separate statement is required for the officer and each spouse and dependent under 18. Add blocks as needed.</p>
          <div class="grid-2">
            <div class="fg"><label>Statement for (describe)</label><input v-model="fs.subject_label" type="text" class="form-control" :disabled="!canEdit"></div>
            <div class="fg"><label>Statement date</label><input v-model="fs.statement_date" type="date" class="form-control" :disabled="!canEdit"></div>
          </div>
          <div class="grid-2">
            <div class="fg"><label>Income period from</label><input v-model="fs.income_period_from" type="date" class="form-control" :disabled="!canEdit"></div>
            <div class="fg"><label>Income period to</label><input v-model="fs.income_period_to" type="date" class="form-control" :disabled="!canEdit"></div>
          </div>
          <h4>Income (incl. emoluments)</h4>
          <table class="mini-table">
            <thead><tr><th>Description</th><th>Approx. amount</th><th v-if="canEdit"></th></tr></thead>
            <tbody>
              <tr v-for="(row, ri) in fs.income_lines" :key="'inc-' + fi + '-' + ri">
                <td><input v-model="row.description" type="text" class="form-control" :disabled="!canEdit"></td>
                <td><input v-model="row.amount" type="text" class="form-control" :disabled="!canEdit"></td>
                <td v-if="canEdit"><button type="button" class="btn-mini" @click="fs.income_lines.splice(ri, 1)">×</button></td>
              </tr>
            </tbody>
          </table>
          <button v-if="canEdit" type="button" class="btn-link" @click="fs.income_lines.push({ description: '', amount: '' })">+ Income line</button>

          <h4>Assets (as of statement date)</h4>
          <table class="mini-table">
            <thead><tr><th>Description (location if applicable)</th><th>Approx. value</th><th v-if="canEdit"></th></tr></thead>
            <tbody>
              <tr v-for="(row, ri) in fs.asset_lines" :key="'ast-' + fi + '-' + ri">
                <td><input v-model="row.description" type="text" class="form-control" :disabled="!canEdit"></td>
                <td><input v-model="row.value" type="text" class="form-control" :disabled="!canEdit"></td>
                <td v-if="canEdit"><button type="button" class="btn-mini" @click="fs.asset_lines.splice(ri, 1)">×</button></td>
              </tr>
            </tbody>
          </table>
          <button v-if="canEdit" type="button" class="btn-link" @click="fs.asset_lines.push({ description: '', value: '' })">+ Asset line</button>

          <h4>Liabilities (as of statement date)</h4>
          <table class="mini-table">
            <thead><tr><th>Description</th><th>Approx. amount</th><th v-if="canEdit"></th></tr></thead>
            <tbody>
              <tr v-for="(row, ri) in fs.liability_lines" :key="'lia-' + fi + '-' + ri">
                <td><input v-model="row.description" type="text" class="form-control" :disabled="!canEdit"></td>
                <td><input v-model="row.amount" type="text" class="form-control" :disabled="!canEdit"></td>
                <td v-if="canEdit"><button type="button" class="btn-mini" @click="fs.liability_lines.splice(ri, 1)">×</button></td>
              </tr>
            </tbody>
          </table>
          <button v-if="canEdit" type="button" class="btn-link" @click="fs.liability_lines.push({ description: '', amount: '' })">+ Liability line</button>

          <div v-if="canEdit && form.financial_statements.length > 1" class="mt-2">
            <button type="button" class="btn-mini danger" @click="form.financial_statements.splice(fi, 1)">Remove this financial statement block</button>
          </div>
        </div>

        <div v-if="canEdit" class="coia-section">
          <button type="button" class="btn-secondary" @click="addFinancialStatement">+ Add financial statement (spouse / dependent)</button>
        </div>

        <div class="coia-section">
          <h3>4. Other relevant information</h3>
          <textarea v-model="form.other_information" class="form-control" rows="3" :disabled="!canEdit"></textarea>
        </div>

        <div class="coia-section">
          <h3>5. Witness details</h3>
          <div class="grid-2">
            <div class="fg"><label>Witness name</label><input v-model="form.witness_name" type="text" class="form-control" :disabled="!canEdit"></div>
            <div class="fg"><label>Witness address</label><input v-model="form.witness_address" type="text" class="form-control" :disabled="!canEdit"></div>
          </div>
        </div>

        <div class="coia-section">
          <h3>6. Declaration and signatures</h3>
          <div class="grid-2">
            <div class="fg">
              <label>Typed signature (declarant)</label>
              <input v-model="form.typed_signature" type="text" class="form-control" :disabled="!canEdit" placeholder="Type your full name">
            </div>
            <div class="fg">
              <label>Signature date</label>
              <input v-model="form.signature_date" type="date" class="form-control" :disabled="!canEdit">
            </div>
          </div>
          <div class="fg">
            <label>Witness signature (typed)</label>
            <input v-model="form.witness_signature" type="text" class="form-control" :disabled="!canEdit" placeholder="Witness full name (typed)">
          </div>
        </div>

        <div class="coia-section solemn" v-if="canEdit">
          <label class="check-row">
            <input v-model="confirmedAccurate" type="checkbox">
            <span>I solemnly declare that the information given is, to the best of my knowledge, true and complete (digital attestation).</span>
          </label>
        </div>

        <div class="card ska-footer-card mt-3">
          <div class="card-body ska-footer-inner">
            <p class="ska-footer-hint mb-3 mb-md-0">
              Save as <strong>draft</strong> while you gather information, or <strong>submit to HR</strong> when complete and attested. After submission, editing is locked until HR rejects and requests correction.
            </p>
            <div class="ska-footer-btns">
              <button type="button" class="ska-btn ska-btn--outline ska-btn--lg" :disabled="saving || !canEdit" @click="saveDraft">{{ saving ? "Saving…" : "Save draft" }}</button>
              <button type="button" class="ska-btn ska-btn--primary ska-btn--lg" :disabled="saving || !canEdit || !confirmedAccurate" @click="submitDeclaration">{{ saving ? "Submitting…" : "Submit to HR" }}</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeCoiaDeclaration",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    const years = [];
    for (let i = y; i >= y - 5; i--) years.push(i);
    return {
      pageMode: "list",
      myRecords: [],
      loadingList: false,
      loadingForm: false,
      saving: false,
      declarationYear: y,
      yearOptions: years,
      status: "none",
      canEdit: true,
      declarationId: null,
      rejectionNote: "",
      confirmedAccurate: false,
      notificationMessage: "",
      form: {}
    };
  },
  computed: {
    pageSubtitle() {
      return this.pageMode === "list"
        ? ""
        : "Digitised template — income, assets and liabilities.";
    },
    statusLabel() {
      const m = {
        none: "Not started",
        Draft: "Draft",
        Pending: "Pending HR / Chief Officer",
        Submitted: "Submitted",
        Approved: "Approved",
        Rejected: "Rejected — resubmit when updated",
        Overdue: "Overdue"
      };
      return m[this.status] || this.status;
    }
  },
  created() {
    this.resetFormShell();
    this.loadMyRecords();
  },
  methods: {
    async loadMyRecords() {
      this.loadingList = true;
      try {
        const fd = new FormData();
        fd.append("function", "listMyCoiaWealthDeclarations");
        const res = await execute(fd, authUrl);
        this.myRecords = res?.data?.success && Array.isArray(res?.data?.data?.records)
          ? res.data.data.records
          : [];
      } catch (_) {
        this.myRecords = [];
      } finally {
        this.loadingList = false;
      }
    },
    startNewDeclaration() {
      const y = new Date().getFullYear();
      this.declarationYear = y;
      this.pageMode = "form";
      this.notificationMessage = "";
      this.loadDeclarationData();
    },
    openRecord(row) {
      if (!row || row.declaration_year == null) return;
      this.declarationYear = Number(row.declaration_year);
      this.pageMode = "form";
      this.notificationMessage = "";
      this.loadDeclarationData();
    },
    backToList() {
      this.pageMode = "list";
      this.notificationMessage = "";
      this.loadMyRecords();
    },
    formatStatus(s) {
      const k = String(s || "").trim().toLowerCase();
      const map = {
        draft: "Draft",
        pending: "Pending approval",
        approved: "Approved",
        rejected: "Rejected",
        submitted: "Submitted"
      };
      return map[k] || (String(s || "").trim() || "—");
    },
    statusPillClass(s) {
      const k = String(s || "").trim().toLowerCase();
      if (k === "draft") return "ska-pill--draft";
      if (k === "rejected") return "ska-pill--returned";
      if (k === "pending") return "ska-pill--submitted";
      if (k === "approved") return "ska-pill--chief";
      return "ska-pill--muted";
    },
    formatDt(v) {
      if (v == null || v === "") return "—";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    },
    isEditableStatus(st) {
      const k = String(st || "").trim().toLowerCase();
      return k === "draft" || k === "rejected";
    },
    resetFormShell() {
      this.form = {
        template: "COIA_2025_S31",
        surname: "",
        first_name: "",
        other_names: "",
        dob: "",
        place_of_birth: "",
        marital_status: "",
        postal_address: "",
        physical_address: "",
        designation: "",
        employer_name: "Homabay County Government",
        nature_of_employment: "Permanent",
        spouses: [{ surname: "", first_name: "", other_names: "" }],
        dependents: [{ surname: "", first_name: "", other_names: "" }],
        financial_statements: [
          {
            subject: "officer",
            subject_label: "Public officer (self)",
            statement_date: "",
            income_period_from: "",
            income_period_to: "",
            income_lines: [{ description: "", amount: "" }],
            asset_lines: [{ description: "", value: "" }],
            liability_lines: [{ description: "", amount: "" }]
          }
        ],
        other_information: "",
        witness_name: "",
        witness_address: "",
        typed_signature: "",
        signature_date: "",
        witness_signature: ""
      };
    },
    loadDeclarationData() {
      this.loadingForm = true;
      const fd = new FormData();
      fd.append("function", "getMyCoiaWealthDeclaration");
      fd.append("declaration_year", this.declarationYear);
      execute(fd, authUrl)
        .then((res) => {
          this.loadingForm = false;
          if (!res.data || !res.data.success || !res.data.data) return;
          const d = res.data.data;
          const nextStatus = d.status || "none";
          const statusKey = `coia_status_${this.declarationYear}`;
          const prevStatus = localStorage.getItem(statusKey) || "";
          if (prevStatus && prevStatus !== nextStatus) {
            if (nextStatus === "Approved") {
              this.notificationMessage = "Your wealth declaration has been approved.";
            } else if (nextStatus === "Rejected") {
              this.notificationMessage = "Your wealth declaration was rejected. Please review and resubmit.";
            } else {
              this.notificationMessage = `Status update: ${prevStatus} -> ${nextStatus}.`;
            }
          } else if (!prevStatus && nextStatus !== "none") {
            this.notificationMessage = `Current status: ${nextStatus}.`;
          }
          localStorage.setItem(statusKey, nextStatus);
          this.status = nextStatus;
          this.canEdit = !!d.can_edit;
          this.declarationId = d.declaration && d.declaration.id ? d.declaration.id : null;
          this.rejectionNote = (d.declaration && d.declaration.remarks) ? String(d.declaration.remarks).trim() : "";
          if (d.form && typeof d.form === "object") {
            this.form = JSON.parse(JSON.stringify(d.form));
          } else {
            this.resetFormShell();
          }
          this.confirmedAccurate = false;
        })
        .catch(() => { this.loadingForm = false; });
    },
    addFinancialStatement() {
      this.form.financial_statements.push({
        subject: "other",
        subject_label: "",
        statement_date: "",
        income_period_from: "",
        income_period_to: "",
        income_lines: [{ description: "", amount: "" }],
        asset_lines: [{ description: "", value: "" }],
        liability_lines: [{ description: "", amount: "" }]
      });
    },
    saveDraft() {
      this.saving = true;
      const payload = JSON.stringify(this.form);
      const fd = new FormData();
      fd.append("function", "saveMyCoiaWealthDeclarationDraft");
      fd.append("declaration_year", this.declarationYear);
      fd.append("form_json", payload);
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            if (res.data.data && res.data.data.id) this.declarationId = res.data.data.id;
            this.loadDeclarationData();
            this.loadMyRecords();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Save failed");
          }
        })
        .catch(() => { this.saving = false; });
    },
    submitDeclaration() {
      if (!this.confirmedAccurate) {
        alert("Confirm the declaration statement before submitting.");
        return;
      }
      if (!String(this.form.typed_signature || "").trim()) {
        alert("Enter your typed signature (full name) before submitting.");
        return;
      }
      if (!String(this.form.signature_date || "").trim()) {
        alert("Select the signature date before submitting.");
        return;
      }
      if (!String(this.form.witness_signature || "").trim()) {
        alert("Enter the witness signature (typed) before submitting.");
        return;
      }
      if (!confirm("Submit to HR? You cannot edit online while it is pending approval.")) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "submitMyCoiaWealthDeclaration");
      fd.append("declaration_year", this.declarationYear);
      if (this.declarationId) fd.append("id", this.declarationId);
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.loadDeclarationData();
            this.loadMyRecords();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Submit failed");
          }
        })
        .catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
/* List / shell (aligned with Skills Audit Assessment) */
.ska-list {
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: 32px;
}
.ska-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 18px;
  box-shadow: 0 8px 28px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.ska-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  opacity: 0.96;
}
.ska-list-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}
.ska-muted {
  color: #64748b;
  font-size: 14px;
  padding: 12px 0;
}
.ska-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 2px 14px rgba(13, 79, 115, 0.06);
}
.ska-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.ska-table thead {
  background: #f8fafc;
}
.ska-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.ska-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}
.ska-table tbody tr:hover {
  background: #f8fafc;
}
.ska-year {
  font-weight: 700;
  color: #0d4f73;
}
.ska-th-actions {
  width: 120px;
}
.ska-actions {
  text-align: right;
  white-space: nowrap;
}
.ska-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
}
.ska-pill {
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.ska-pill--inline {
  font-size: 12px;
  padding: 6px 12px;
}
.ska-pill--draft {
  background: #fef3c7;
  color: #92400e;
}
.ska-pill--returned {
  background: #fee2e2;
  color: #991b1b;
}
.ska-pill--submitted {
  background: #e0f2fe;
  color: #0369a1;
}
.ska-pill--chief {
  background: #ecfdf5;
  color: #047857;
}
.ska-pill--muted {
  background: #f1f5f9;
  color: #64748b;
}

.ska-form {
  max-width: 920px;
  margin: 0 auto;
  padding-bottom: 40px;
}
.ska-toolbar-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 12px rgba(13, 79, 115, 0.06);
}
.ska-footer-card {
  border-radius: 14px;
  border: 1px solid #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
  box-shadow: 0 4px 20px rgba(13, 79, 115, 0.08);
}
.ska-footer-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.ska-footer-hint {
  flex: 1;
  min-width: 220px;
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}
.ska-footer-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ska-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}
.ska-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.ska-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}
.ska-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}
.ska-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid transparent;
}
.ska-btn--ghost:hover:not(:disabled) {
  background: #f1f5f9;
}
.ska-btn--sm {
  padding: 6px 12px;
  font-size: 13px;
}
.ska-btn--lg {
  padding: 12px 22px;
  font-size: 15px;
  border-radius: 11px;
}

.coia-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #eef1f4;
}
.coia-section h3 {
  font-size: 15px;
  margin: 0 0 12px;
  color: #0d4f73;
}
.coia-section h4 {
  font-size: 13px;
  margin: 12px 0 8px;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 720px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
.fg {
  margin-bottom: 10px;
}
.fg label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}
.form-control {
  width: 100%;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
}
.person-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
@media (max-width: 720px) {
  .person-row {
    grid-template-columns: 1fr;
  }
}
.mini-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;
  margin-bottom: 8px;
}
.mini-table th,
.mini-table td {
  border: 1px solid #dee2e6;
  padding: 6px;
  vertical-align: middle;
}
.hint {
  font-size: 12px;
  color: #6c757d;
  margin: 0 0 10px;
}
.btn-link {
  background: none;
  border: none;
  color: #0d6efd;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
}
.btn-mini {
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}
.btn-mini.danger {
  color: #842029;
  border-color: #f1aeb5;
}
.solemn {
  background: #f8f9fa;
}
.check-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  cursor: pointer;
}
.fin-block {
  border-left: 3px solid #1478b0;
}
.mt-2 {
  margin-top: 8px;
}
.btn-secondary {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  cursor: pointer;
  font-size: 14px;
}
.notice-readonly {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #78350f;
}
.notice-rejected {
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #7f1d1d;
}
.notice-status-change {
  background: #e8f4fc;
  border: 1px solid #bae6fd;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #0d4f73;
}
</style>
